// src/data/readySystems.js
import React from 'react';
import {
  // Icons import
  FaHome,
  FaStore,
  FaBalanceScale,
  FaTruck,
  FaGlobeAfrica,
  FaCut,
  FaUserCheck,
  FaTools,
  FaChartBar,
  FaSync,
  FaSearch,
  FaMobileAlt,
  FaCalendarAlt,
  FaCreditCard,
  FaCogs,
  FaDatabase,
  FaUsers,
  FaCloud,
  FaLock,
  FaChartLine,
  FaUtensils,
  FaHeartbeat,
  FaSeedling} from 'react-icons/fa';

  export const readySystems = [
    {
      id: 1,
      name: "Tour Management System",
      category: "Tourism",
      icon: <FaGlobeAfrica />,
      color: "#7c3aed",
      description: "Tourism business management system for tour packages, bookings, and customer engagement",
      challenge: "Tour operators struggling with package management and customer bookings",
      solution: "Comprehensive tourism management platform with multi-language support",
      features: [
        { icon: <FaCalendarAlt />, text: "Tour package management" },
        { icon: <FaCreditCard />, text: "Online booking system" },
        { icon: <FaUsers />, text: "Guide management portal" },
        { icon: <FaTools />, text: "Payment processing" },
        { icon: <FaChartBar />, text: "Customer review system" },
        { icon: <FaSync />, text: "Multi-language support" }
      ],
      industries: ["Tour Operators", "Travel Guides", "Adventure Companies", "Cultural Tours"],
      implementationTime: "3-4 weeks",
      popular: true,
      featured: true,
      tags: ["Tourism", "Travel", "Booking", "Packages"],
      results: [
        { metric: "70%", label: "Booking Growth" },
        { metric: "4.8★", label: "Customer Rating" },
        { metric: "50%", label: "Admin Time Saved" }
      ],
      duration: "4 Weeks"
    },
    {
      id: 2,
      name: "E-commerce with Sales & Admin Dashboard (CRM)",
      category: "E-commerce",
      icon: <FaStore />,
      color: "#f59e0b",
      description: "Complete e-commerce platform with integrated CRM and admin dashboard for sales management",
      challenge: "Manual sales tracking and disconnected customer data affecting business growth",
      solution: "Integrated e-commerce with real-time inventory, CRM, and analytics",
      features: [
        { icon: <FaDatabase />, text: "Real-time inventory tracking" },
        { icon: <FaCreditCard />, text: "POS with multiple payment options" },
        { icon: <FaUsers />, text: "Customer relationship management" },
        { icon: <FaUserCheck />, text: "Customer loyalty program" },
        { icon: <FaChartBar />, text: "Sales reports and analytics" },
        { icon: <FaTools />, text: "Barcode and QR code integration" }
      ],
      industries: ["Retail Shops", "Supermarkets", "Boutiques", "Wholesalers"],
      implementationTime: "2-3 weeks",
      featured: true,
      tags: ["E-commerce", "Inventory", "POS", "CRM", "Analytics"],
      results: [
        { metric: "75%", label: "Inventory Accuracy" },
        { metric: "50%", label: "Checkout Speed" },
        { metric: "30%", label: "Sales Growth" }
      ],
      duration: "3 Weeks"
    },
    {
      id: 3,
      name: "Restaurant Management System (POS)",
      category: "Restaurant & Food Service",
      icon: <FaUtensils />,
      color: "#ef4444",
      description: "Complete restaurant management system with POS, inventory, and online ordering",
      challenge: "Restaurants struggling with manual orders, inventory tracking, and customer management",
      solution: "Integrated POS with online ordering and kitchen display system",
      features: [
        { icon: <FaDatabase />, text: "Menu management with real-time updates" },
        { icon: <FaCreditCard />, text: "POS with multiple payment options" },
        { icon: <FaCalendarAlt />, text: "Table reservation system" },
        { icon: <FaTools />, text: "Kitchen display system" },
        { icon: <FaMobileAlt />, text: "Mobile ordering app" },
        { icon: <FaChartBar />, text: "Inventory and cost analysis" }
      ],
      industries: ["Restaurants", "Hotels", "Cafés", "Food Trucks"],
      implementationTime: "3-4 weeks",
      popular: true,
      tags: ["Restaurant", "POS", "Food Service", "Ordering"],
      results: [
        { metric: "40%", label: "Order Accuracy" },
        { metric: "50%", label: "Service Speed" },
        { metric: "35%", label: "Revenue Growth" }
      ],
      duration: "4 Weeks"
    },
    {
      id: 4,
      name: "Property Management System",
      category: "Real Estate",
      icon: <FaHome />,
      color: "#3b82f6",
      description: "Property management platform for rentals, sales, and tenant management",
      challenge: "Disconnected systems for property listing, tenant communication, and rent collection",
      solution: "Unified platform with virtual tours and automated rent collection",
      features: [
        { icon: <FaDatabase />, text: "Property listing management" },
        { icon: <FaUserCheck />, text: "Tenant portal and communication" },
        { icon: <FaCreditCard />, text: "Automated rent collection" },
        { icon: <FaTools />, text: "Maintenance request system" },
        { icon: <FaCloud />, text: "Document management and storage" },
        { icon: <FaChartBar />, text: "Reporting and analytics dashboard" }
      ],
      industries: ["Property Managers", "Real Estate Agencies", "Landlords", "Developers"],
      implementationTime: "3-4 weeks",
      tags: ["Real Estate", "Property", "Rental", "Management"],
      results: [
        { metric: "80%", label: "Tenant Satisfaction" },
        { metric: "99%", label: "Rent Collection" },
        { metric: "60%", label: "Viewing Efficiency" }
      ],
      duration: "3 Weeks"
    },
    {
      id: 5,
      name: "Law Firm Management System",
      category: "Legal Services",
      icon: <FaBalanceScale />,
      color: "#1e40af",
      description: "Law firm management system for case tracking, client management, and document automation",
      challenge: "Manual case management leading to missed deadlines and poor client communication",
      solution: "Automated case management with secure client portal",
      features: [
        { icon: <FaDatabase />, text: "Case management and tracking" },
        { icon: <FaUserCheck />, text: "Secure client portal" },
        { icon: <FaTools />, text: "Document template automation" },
        { icon: <FaCreditCard />, text: "Automated billing and invoicing" },
        { icon: <FaCalendarAlt />, text: "Court date and deadline calendar" },
        { icon: <FaLock />, text: "Encrypted messaging system" }
      ],
      industries: ["Law Firms", "Legal Consultants", "Advocates", "Paralegal Services"],
      implementationTime: "4-5 weeks",
      tags: ["Legal", "Law", "Case Management", "Documentation"],
      results: [
        { metric: "90%", label: "Case Organization" },
        { metric: "40%", label: "Prep Time" },
        { metric: "100%", label: "Deadline Compliance" }
      ],
      duration: "5 Weeks"
    },
    {
      id: 6,
      name: "Logistics Management System",
      category: "Logistics",
      icon: <FaTruck />,
      color: "#059669",
      description: "Logistics and supply chain management system for tracking and optimization",
      challenge: "Poor shipment tracking and inefficient route planning",
      solution: "GPS-enabled tracking with AI-powered route optimization",
      features: [
        { icon: <FaSearch />, text: "Real-time shipment tracking" },
        { icon: <FaTruck />, text: "Fleet management and maintenance" },
        { icon: <FaCogs />, text: "AI route optimization" },
        { icon: <FaUsers />, text: "Driver performance monitoring" },
        { icon: <FaMobileAlt />, text: "Customer notification system" },
        { icon: <FaChartBar />, text: "Delivery analytics dashboard" }
      ],
      industries: ["Courier Services", "Transport Companies", "Supply Chain", "Distribution"],
      implementationTime: "4-5 weeks",
      tags: ["Logistics", "Shipping", "Tracking", "Optimization"],
      results: [
        { metric: "65%", label: "Fuel Cost Reduction" },
        { metric: "85%", label: "On-time Deliveries" },
        { metric: "50%", label: "Route Efficiency" }
      ],
      duration: "4 Weeks"
    },
    {
      id: 7,
      name: "Healthcare Management System (EHR)",
      category: "Healthcare",
      icon: <FaHeartbeat />,
      color: "#dc2626",
      description: "Comprehensive healthcare management system for clinics, hospitals, and medical practices",
      challenge: "Manual patient records, appointment scheduling, and billing processes causing inefficiencies",
      solution: "Integrated electronic health records with telemedicine capabilities",
      features: [
        { icon: <FaDatabase />, text: "Electronic Health Records (EHR)" },
        { icon: <FaCalendarAlt />, text: "Smart appointment scheduling" },
        { icon: <FaUserCheck />, text: "Patient portal and telemedicine" },
        { icon: <FaCreditCard />, text: "Insurance and billing management" },
        { icon: <FaTools />, text: "Lab test integration" },
        { icon: <FaLock />, text: "HIPAA compliant security" }
      ],
      industries: ["Hospitals", "Clinics", "Medical Centers", "Private Practices"],
      implementationTime: "5-6 weeks",
      featured: true,
      tags: ["Healthcare", "EHR", "Telemedicine", "Medical"],
      results: [
        { metric: "70%", label: "Patient Satisfaction" },
        { metric: "60%", label: "Admin Efficiency" },
        { metric: "100%", label: "Record Accuracy" }
      ],
      duration: "6 Weeks"
    },
    {
      id: 8,
      name: "Real Estate Listing Platform",
      category: "Real Estate",
      icon: <FaHome />,
      color: "#3b82f6",
      description: "Real estate listing and property showcase platform",
      challenge: "Real estate agencies needed modern online presence and property showcase",
      solution: "Professional property listing platform with virtual tours and mapping",
      features: [
        { icon: <FaDatabase />, text: "Property listing management" },
        { icon: <FaSearch />, text: "Virtual tour integration" },
        { icon: <FaUserCheck />, text: "Agent profile system" },
        { icon: <FaTools />, text: "Mortgage calculator" },
        { icon: <FaGlobeAfrica />, text: "Location mapping" },
        { icon: <FaMobileAlt />, text: "Inquiry management system" }
      ],
      industries: ["Real Estate Agencies", "Property Developers", "Individual Agents"],
      implementationTime: "3-4 weeks",
      tags: ["Real Estate", "Property", "Listings", "Showcase"],
      results: [
        { metric: "3x", label: "Property Views" },
        { metric: "40%", label: "Lead Conversion" },
        { metric: "60%", label: "Inquiry Response Time" }
      ],
      duration: "3 Weeks"
    },
    {
      id: 9,
      name: "Farm Management System (AgriTech)",
      category: "Agriculture",
      icon: <FaSeedling />,
      color: "#22c55e",
      description: "Agricultural management system for farmers, cooperatives, and agribusinesses",
      challenge: "Farmers struggling with crop planning, inventory, and market access",
      solution: "Integrated farm management with weather integration and market linkage",
      features: [
        { icon: <FaCalendarAlt />, text: "Crop planning and scheduling" },
        { icon: <FaDatabase />, text: "Inventory and resource management" },
        { icon: <FaChartBar />, text: "Yield prediction analytics" },
        { icon: <FaTools />, text: "Weather integration and alerts" },
        { icon: <FaUsers />, text: "Marketplace for produce sales" },
        { icon: <FaMobileAlt />, text: "Mobile app for field workers" }
      ],
      industries: ["Farmers", "Cooperatives", "Agribusinesses", "Agricultural Consultants"],
      implementationTime: "4-5 weeks",
      tags: ["Agriculture", "Farming", "AgriTech", "Farm Management"],
      results: [
        { metric: "35%", label: "Yield Increase" },
        { metric: "50%", label: "Cost Reduction" },
        { metric: "60%", label: "Market Access" }
      ],
      duration: "5 Weeks"
    },
    {
      id: 10,
      name: "Salon & Spa Management System",
      category: "Beauty & Wellness",
      icon: <FaCut />,
      color: "#ec4899",
      description: "Complete beauty and wellness management suite for modern salons",
      challenge: "Beauty businesses needed integrated solution for operations and marketing",
      solution: "All-in-one beauty business management platform",
      features: [
        { icon: <FaCalendarAlt />, text: "Appointment booking system" },
        { icon: <FaUserCheck />, text: "Client management CRM" },
        { icon: <FaDatabase />, text: "Product inventory management" },
        { icon: <FaUsers />, text: "Staff scheduling" },
        { icon: <FaTools />, text: "Marketing automation" },
        { icon: <FaChartLine />, text: "Business analytics" }
      ],
      industries: ["Beauty Salons", "Spa Centers", "Wellness Clinics", "Barbershops"],
      implementationTime: "2-3 weeks",
      popular: true,
      tags: ["Beauty", "Wellness", "Scheduling", "CRM"],
      results: [
        { metric: "65%", label: "Client Retention" },
        { metric: "40%", label: "Revenue Growth" },
        { metric: "80%", label: "Appointment Fill Rate" }
      ],
      duration: "3 Weeks"
    },
    {
      id: 11,
      name: "Hotel & Camp Management System",
      category: "Hospitality",
      icon: <FaHome />,
      color: "#10b981",
      description: "Complete camp and hotel management system for safari lodges and hospitality businesses",
      challenge: "Manual booking systems causing double bookings and poor guest experience",
      solution: "Integrated booking, payment, and guest management platform",
      features: [
        { icon: <FaCalendarAlt />, text: "Real-time room booking system" },
        { icon: <FaUserCheck />, text: "Complete guest management suite" },
        { icon: <FaCreditCard />, text: "Secure payment processing" },
        { icon: <FaCogs />, text: "Activity scheduling and management" },
        { icon: <FaDatabase />, text: "Inventory and supply chain tracking" },
        { icon: <FaChartBar />, text: "Staff performance analytics" }
      ],
      industries: ["Safari Lodges", "Hotels", "Camps", "Resorts"],
      implementationTime: "3-4 weeks",
      popular: true,
      tags: ["Hospitality", "Booking", "Management", "Tourism"],
      results: [
        { metric: "60%", label: "Booking Efficiency" },
        { metric: "95%", label: "Guest Satisfaction" },
        { metric: "45%", label: "Staff Productivity" }
      ],
      duration: "4 Weeks"
    }
  ];

// Helper function to get system by ID
export const getSystemById = (id) => {
  return readySystems.find(system => system.id === id);
};

// Helper function to get featured systems
export const getFeaturedSystems = () => {
  return readySystems.filter(system => system.featured);
};

// Helper function to get popular systems
export const getPopularSystems = () => {
  return readySystems.filter(system => system.popular);
};

// Helper function to get systems by category
export const getSystemsByCategory = (category) => {
  return readySystems.filter(system => system.category === category);
};

// Get all unique categories
export const getAllCategories = () => {
  const categories = readySystems.map(system => system.category);
  return [...new Set(categories)];
};

// Get systems with price range
export const getSystemsByPriceRange = (min, max) => {
  return readySystems.filter(system => 
    system.startingPrice >= min && system.startingPrice <= max
  );
};

// Get systems by tag
export const getSystemsByTag = (tag) => {
  return readySystems.filter(system => 
    system.tags.includes(tag)
  );
};

export default readySystems;